import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as categoryApi from '../../apis/lessonCategories';

interface CategoryState {
    categories: any[];
    currentCategory: any | null;
    loading: boolean;
    error: string | null;
    pagination: {
        total: number;
        page: number;
        size: number;
        pages: number;
    };
}

const initialState: CategoryState = {
    categories: [],
    currentCategory: null,
    loading: false,
    error: null,
    pagination: {
        total: 0,
        page: 1,
        size: 10,
        pages: 0
    }
};

export const fetchCategories = createAsyncThunk(
    'lessonCategories/fetchAll',
    async (params: any, { rejectWithValue }) => {
        try {
            const response = await categoryApi.getLessonCategories(params);
            return response.data;
        } catch (err: any) {
            return rejectWithValue(err.response?.data?.detail || 'Failed to fetch categories');
        }
    }
);

export const fetchCategoryDetail = createAsyncThunk(
    'lessonCategories/fetchDetail',
    async (id: string, { rejectWithValue }) => {
        try {
            const response = await categoryApi.getLessonCategory(id);
            return response.data;
        } catch (err: any) {
            return rejectWithValue(err.response?.data?.detail || 'Failed to fetch category detail');
        }
    }
);

export const addCategory = createAsyncThunk(
    'lessonCategories/add',
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await categoryApi.createLessonCategory(data);
            return response.data;
        } catch (err: any) {
            return rejectWithValue(err.response?.data?.detail || 'Failed to add category');
        }
    }
);

export const editCategory = createAsyncThunk(
    'lessonCategories/edit',
    async ({ id, data }: { id: string, data: any }, { rejectWithValue }) => {
        try {
            const response = await categoryApi.updateLessonCategory(id, data);
            return response.data;
        } catch (err: any) {
            return rejectWithValue(err.response?.data?.detail || 'Failed to update category');
        }
    }
);

export const removeCategory = createAsyncThunk(
    'lessonCategories/remove',
    async (id: string, { rejectWithValue }) => {
        try {
            await categoryApi.deleteLessonCategory(id);
            return id;
        } catch (err: any) {
            return rejectWithValue(err.response?.data?.detail || 'Failed to delete category');
        }
    }
);

const lessonCategorySlice = createSlice({
    name: 'lessonCategories',
    initialState,
    reducers: {
        clearCurrentCategory: (state) => {
            state.currentCategory = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch all
            .addCase(fetchCategories.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCategories.fulfilled, (state, action) => {
                state.loading = false;
                const payload = action.payload;
                if (Array.isArray(payload)) {
                    state.categories = payload;
                    state.pagination = { total: payload.length, page: 1, size: payload.length, pages: 1 };
                } else if (payload && Array.isArray(payload.items)) {
                    state.categories = payload.items;
                    state.pagination = {
                        total: payload.total || 0,
                        page: payload.page || 1,
                        size: payload.size || 10,
                        pages: payload.pages || 0
                    };
                } else {
                    state.categories = [];
                }
            })
            .addCase(fetchCategories.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            // Fetch detail
            .addCase(fetchCategoryDetail.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchCategoryDetail.fulfilled, (state, action) => {
                state.loading = false;
                state.currentCategory = action.payload;
            })
            // Remove
            .addCase(removeCategory.fulfilled, (state, action) => {
                state.categories = state.categories.filter(c => c.id !== action.payload);
            });
    }
});

export const { clearCurrentCategory } = lessonCategorySlice.actions;
export default lessonCategorySlice.reducer;
